import { SvelteMap } from 'svelte/reactivity'

/*Breadcrumb needs to have the following data:
{
		title: string, -> label that is shown
		data: object, -> the object and other data needed to handle navigating and to give back with the callback
		callback: function(data, index), -> function from the caller to execute when clicking on the breadcrumb
}*/
export type Breadcrumb = {
	id?: string
	title: string
	data: any // don't need to know the type of the data
	/**
	 * callback function that is called when the breadcrumb is clicked
	 * @param data the data that was passed to the breadcrumb
	 * @param index the index of the clicked breadcrumb
	 */
	callback: (data: any, index: number) => void
}

class BreadcrumbContexts {
	#map = $state<SvelteMap<string, Breadcrumb[]>>(new SvelteMap([['default', []]]))

	/** Read the current list for a context (reactive in Svelte 5). */
	get(context = 'default'): Breadcrumb[] {
		return this.#map.get(context) ?? []
	}

	/** Append an item to a context. */
	push(item: Breadcrumb, context = 'default'): void {
		this.#map.set(context, [...(this.#map.get(context) ?? []), item])
	}

	/** Remove and return the last item of a context. */
	pop(context = 'default'): Breadcrumb | undefined {
		const current = this.#map.get(context) ?? []
		if (current.length === 0) return undefined
		const popped = current.at(-1)!
		this.#map.set(context, current.slice(0, -1))
		return popped
	}

	/** Replace the entire list for a context. */
	setContext(items: Breadcrumb[], context = 'default'): void {
		this.#map.set(context, items)
	}

	/** Clear a context (set to empty list). */
	clear(context = 'default'): void {
		this.#map.set(context, [])
	}

	/** Whether a context key exists in the map (distinct from having an empty list). */
	has(context = 'default'): boolean {
		return this.#map.has(context)
	}

	/** Remove a context entry entirely (frees the key, not just the list). */
	remove(context = 'default'): void {
		this.#map.delete(context)
	}

	/** Trim a context so only items at indices 0..index remain. */
	popUntil(index: number, context = 'default'): void {
		const current = this.#map.get(context) ?? []
		this.#map.set(context, current.slice(0, index + 1))
	}

	/** Low-level bulk update (used by pushBreadcrumb and fsm). */
	update(fn: (m: SvelteMap<string, Breadcrumb[]>) => SvelteMap<string, Breadcrumb[]>): void {
		this.#map = fn(this.#map)
	}
}

export const breadcrumbs = new BreadcrumbContexts()

// ---------------------------------------------------------------------------
// pushBreadcrumb — auto-assigns id and appends to any context
// ---------------------------------------------------------------------------

export function pushBreadcrumb(item: Omit<Breadcrumb, 'id'>, context = 'default'): string {
	const id = crypto.randomUUID()
	breadcrumbs.push({ ...item, id } as Breadcrumb, context)
	return id
}
